/**
 * A small, safe formula language for database properties (no `eval`): it can
 * only read the row's values and compute with them.
 *
 *   days_until(prop("Deadline"))
 *   if(prop("Status") == "Done", "✓", days_until(prop("Deadline")) + " dagen")
 *   round(prop("Uren") * 1.21, 2)
 *
 * Operators: + - * / %, == != < > <= >=, && || ! (and, or, not), parentheses.
 * Values: numbers, "text", true/false, dates as "YYYY-MM-DD" text, lists.
 */
import { splitDateTime } from './tasks';

export type FormulaValue = number | string | boolean | null | FormulaValue[];

export class FormulaError extends Error {}

export interface FormulaContext {
  /** A property value by its name (case-insensitive); the title is "title"/"Naam". */
  prop(name: string): unknown;
  /** Today as YYYY-MM-DD (local). */
  today: string;
}

type Token =
  | { t: 'num'; v: number }
  | { t: 'str'; v: string }
  | { t: 'id'; v: string }
  | { t: 'op'; v: string };

const OPS = [
  '==',
  '!=',
  '<=',
  '>=',
  '&&',
  '||',
  '+',
  '-',
  '*',
  '/',
  '%',
  '<',
  '>',
  '!',
  '(',
  ')',
  ',',
];

function tokenize(src: string): Token[] {
  const out: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (/[0-9.]/.test(c)) {
      const m = /^\d*\.?\d+/.exec(src.slice(i));
      if (!m) throw new FormulaError(`Onverwacht teken "${c}"`);
      out.push({ t: 'num', v: Number(m[0]) });
      i += m[0].length;
      continue;
    }
    if (c === '"' || c === "'" || c === '“') {
      const close = c === '“' ? '”' : c;
      let j = i + 1;
      let v = '';
      while (j < src.length && src[j] !== close) {
        if (src[j] === '\\' && j + 1 < src.length) j++;
        v += src[j];
        j++;
      }
      if (j >= src.length) throw new FormulaError('Tekst zonder afsluitend aanhalingsteken');
      out.push({ t: 'str', v });
      i = j + 1;
      continue;
    }
    if (/[\p{L}_]/u.test(c)) {
      const m = /^[\p{L}_][\p{L}\p{N}_]*/u.exec(src.slice(i))!;
      out.push({ t: 'id', v: m[0] });
      i += m[0].length;
      continue;
    }
    const op = OPS.find((o) => src.startsWith(o, i));
    if (!op) throw new FormulaError(`Onverwacht teken "${c}"`);
    out.push({ t: 'op', v: op });
    i += op.length;
  }
  return out;
}

type Node =
  | { k: 'lit'; v: FormulaValue }
  | { k: 'call'; name: string; args: Node[] }
  | { k: 'un'; op: string; a: Node }
  | { k: 'bin'; op: string; a: Node; b: Node };

/** Parse once; evaluate many times. */
export function parseFormula(src: string): Node {
  const tokens = tokenize(src);
  let pos = 0;
  const peek = () => tokens[pos];
  const isOp = (...ops: string[]) => {
    const t = peek();
    return (
      (t?.t === 'op' && ops.includes(t.v)) || (t?.t === 'id' && ops.includes(t.v.toLowerCase()))
    );
  };
  const take = () => tokens[pos++]!;
  const expect = (v: string) => {
    if (!isOp(v)) throw new FormulaError(`"${v}" verwacht`);
    pos++;
  };
  const NORMAL: Record<string, string> = { and: '&&', or: '||', not: '!' };
  const opOf = (t: Token) => {
    const v = String(t.v);
    return NORMAL[v.toLowerCase()] ?? v;
  };

  const binary = (next: () => Node, ops: string[]) => (): Node => {
    let a = next();
    while (isOp(...ops)) {
      const op = opOf(take());
      a = { k: 'bin', op, a, b: next() };
    }
    return a;
  };
  const primary = (): Node => {
    const t = peek();
    if (!t) throw new FormulaError('Formule is onvolledig');
    if (t.t === 'num' || t.t === 'str') {
      pos++;
      return { k: 'lit', v: t.v };
    }
    if (t.t === 'op' && t.v === '(') {
      pos++;
      const e = or();
      expect(')');
      return e;
    }
    if (t.t === 'id') {
      pos++;
      const name = t.v.toLowerCase();
      if (name === 'true' || name === 'waar') return { k: 'lit', v: true };
      if (name === 'false' || name === 'onwaar') return { k: 'lit', v: false };
      expect('(');
      const args: Node[] = [];
      if (!isOp(')')) {
        args.push(or());
        while (isOp(',')) {
          pos++;
          args.push(or());
        }
      }
      expect(')');
      return { k: 'call', name, args };
    }
    throw new FormulaError(`Onverwacht "${t.v}"`);
  };
  const unary = (): Node => {
    if (isOp('-', '!', 'not')) {
      const op = opOf(take());
      return { k: 'un', op, a: unary() };
    }
    return primary();
  };
  const mul = binary(unary, ['*', '/', '%']);
  const add = binary(mul, ['+', '-']);
  const cmp = binary(add, ['==', '!=', '<', '>', '<=', '>=']);
  const and = binary(cmp, ['&&', 'and']);
  const or = binary(and, ['||', 'or']);

  const tree = or();
  if (pos < tokens.length) throw new FormulaError(`Onverwacht "${String(tokens[pos]!.v)}"`);
  return tree;
}

// ---- evaluation -------------------------------------------------------------------------

const DAY = 86_400_000;
const dayOf = (v: unknown): string | null =>
  typeof v === 'string' ? (splitDateTime(v)?.date ?? null) : null;
const epochDay = (date: string) =>
  Date.UTC(+date.slice(0, 4), +date.slice(5, 7) - 1, +date.slice(8, 10)) / DAY;
const fromEpochDay = (n: number) => new Date(n * DAY).toISOString().slice(0, 10);

function toNumber(v: FormulaValue): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (v === null || v === '') return 0;
  const n = Number(v);
  if (Number.isNaN(n)) throw new FormulaError(`"${String(v)}" is geen getal`);
  return n;
}
const toText = (v: FormulaValue): string =>
  v === null ? '' : Array.isArray(v) ? v.map(toText).join(', ') : String(v);
const truthy = (v: FormulaValue) =>
  Array.isArray(v) ? v.length > 0 : v !== null && v !== false && v !== '' && v !== 0;
const isEmpty = (v: FormulaValue) => v === null || v === '' || (Array.isArray(v) && !v.length);

function normalize(v: unknown): FormulaValue {
  if (v === undefined || v === null) return null;
  if (typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean') return v;
  if (Array.isArray(v)) return v.map(normalize);
  return String(v);
}

function compare(a: FormulaValue, b: FormulaValue): number {
  if (typeof a === 'number' || typeof b === 'number') return toNumber(a) - toNumber(b);
  return toText(a).localeCompare(toText(b), undefined, { numeric: true });
}

function call(name: string, args: FormulaValue[], ctx: FormulaContext): FormulaValue {
  const arg = (i: number) => args[i] ?? null;
  const date = (i: number) => {
    const d = dayOf(arg(i));
    return d;
  };
  switch (name) {
    case 'prop':
      return normalize(ctx.prop(toText(arg(0))));
    case 'if':
      return truthy(arg(0)) ? arg(1) : arg(2);
    case 'today':
    case 'now':
      return ctx.today;
    case 'days_until': {
      const d = date(0);
      return d ? epochDay(d) - epochDay(ctx.today) : null;
    }
    case 'days_since': {
      const d = date(0);
      return d ? epochDay(ctx.today) - epochDay(d) : null;
    }
    case 'days_between': {
      const a = date(0);
      const b = date(1);
      return a && b ? epochDay(b) - epochDay(a) : null;
    }
    case 'date_add': {
      const d = date(0);
      if (!d) return null;
      const n = toNumber(arg(1));
      const unit = toText(arg(2) ?? 'days').toLowerCase();
      if (/^(week|weeks|weken)$/.test(unit)) return fromEpochDay(epochDay(d) + 7 * n);
      if (/^(month|months|maand|maanden)$/.test(unit)) {
        const dt = new Date(`${d}T12:00:00Z`);
        dt.setUTCMonth(dt.getUTCMonth() + n);
        return dt.toISOString().slice(0, 10);
      }
      return fromEpochDay(epochDay(d) + n);
    }
    case 'empty':
      return isEmpty(arg(0));
    case 'length': {
      const v = arg(0);
      return Array.isArray(v) ? v.length : toText(v).length;
    }
    case 'concat':
      return args.map(toText).join('');
    case 'contains':
      return toText(arg(0))
        .toLowerCase()
        .includes(toText(arg(1)).toLowerCase());
    case 'lower':
      return toText(arg(0)).toLowerCase();
    case 'upper':
      return toText(arg(0)).toUpperCase();
    case 'round': {
      // Shift via the decimal notation, so 9.075 rounds to 9.08 (not 9.07 as
      // 9.075 * 100 = 907.4999… in binary floating point would).
      const digits = Math.trunc(toNumber(arg(1) ?? 0));
      const shifted = Math.round(Number(`${toNumber(arg(0))}e${digits}`));
      return Number(`${shifted}e${-digits}`);
    }
    case 'floor':
      return Math.floor(toNumber(arg(0)));
    case 'ceil':
      return Math.ceil(toNumber(arg(0)));
    case 'abs':
      return Math.abs(toNumber(arg(0)));
    case 'min':
      return Math.min(...args.flat().map(toNumber));
    case 'max':
      return Math.max(...args.flat().map(toNumber));
    default:
      throw new FormulaError(`Onbekende functie ${name}()`);
  }
}

function evaluate(node: Node, ctx: FormulaContext): FormulaValue {
  switch (node.k) {
    case 'lit':
      return node.v;
    case 'call':
      return call(
        node.name,
        node.args.map((a) => evaluate(a, ctx)),
        ctx,
      );
    case 'un': {
      const a = evaluate(node.a, ctx);
      return node.op === '-' ? -toNumber(a) : !truthy(a);
    }
    case 'bin': {
      if (node.op === '&&') return truthy(evaluate(node.a, ctx)) && truthy(evaluate(node.b, ctx));
      if (node.op === '||') return truthy(evaluate(node.a, ctx)) || truthy(evaluate(node.b, ctx));
      const a = evaluate(node.a, ctx);
      const b = evaluate(node.b, ctx);
      switch (node.op) {
        case '+':
          return typeof a === 'string' || typeof b === 'string'
            ? toText(a) + toText(b)
            : toNumber(a) + toNumber(b);
        case '-':
          return toNumber(a) - toNumber(b);
        case '*':
          return toNumber(a) * toNumber(b);
        case '/': {
          const d = toNumber(b);
          if (d === 0) throw new FormulaError('Delen door nul');
          return toNumber(a) / d;
        }
        case '%':
          return toNumber(a) % toNumber(b);
        case '==':
          return compare(a, b) === 0;
        case '!=':
          return compare(a, b) !== 0;
        case '<':
          return compare(a, b) < 0;
        case '>':
          return compare(a, b) > 0;
        case '<=':
          return compare(a, b) <= 0;
        default:
          return compare(a, b) >= 0;
      }
    }
  }
}

const cache = new Map<string, Node | FormulaError>();

/** Evaluate an expression; errors become a FormulaError value (shown as "#FOUT"). */
export function runFormula(expression: string, ctx: FormulaContext): FormulaValue | FormulaError {
  let tree = cache.get(expression);
  if (!tree) {
    try {
      tree = parseFormula(expression);
    } catch (e) {
      tree = e instanceof FormulaError ? e : new FormulaError(String(e));
    }
    if (cache.size > 500) cache.clear();
    cache.set(expression, tree);
  }
  if (tree instanceof FormulaError) return tree;
  try {
    const v = evaluate(tree, ctx);
    return typeof v === 'number' && !Number.isFinite(v) ? null : v;
  } catch (e) {
    return e instanceof FormulaError ? e : new FormulaError(String(e));
  }
}
